import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, MessagesSquare, Phone, Mail, HelpCircle } from "lucide-react";

// Contact Us — light CRM page (mockup: 4 support-channel cards + other
// enquiries strip). Rendered inside PortalShell, so no navbar/footer here.
// Real channels: WhatsApp support chat + call on +91 78400 99836, and
// connect@jurident.com for email/enquiries.
const SUPPORT_PHONE_DISPLAY = "+91 78400 99836";
const SUPPORT_PHONE_TEL = "+917840099836";
const SUPPORT_WHATSAPP = "https://wa.me/7840099836";
const SUPPORT_EMAIL = "connect@jurident.com";

function ContactUs() {
  const navigate = useNavigate();

  const cards = [
    {
      key: "chat",
      title: "Support Chat",
      lines: ["Chat with our support team", "24/7 online support"],
      icon: <MessagesSquare className="h-6 w-6" />,
      tint: "bg-indigo-50 text-indigo-600",
      cta: "Start Chat",
      primary: true,
      onClick: () => window.open(SUPPORT_WHATSAPP, "_blank", "noopener,noreferrer"),
    },
    {
      key: "call",
      title: "Call Center",
      lines: ["Speak with our support team", SUPPORT_PHONE_DISPLAY],
      icon: <Phone className="h-6 w-6" />,
      tint: "bg-orange-50 text-orange-500",
      cta: "Call Now",
      onClick: () => window.open(`tel:${SUPPORT_PHONE_TEL}`),
    },
    {
      key: "email",
      title: "Email Support",
      lines: ["Send us an email and we will", "respond shortly"],
      linkText: SUPPORT_EMAIL,
      linkHref: `mailto:${SUPPORT_EMAIL}`,
      icon: <Mail className="h-6 w-6" />,
      tint: "bg-blue-50 text-blue-600",
      cta: "Send Email",
      onClick: () => window.open(`mailto:${SUPPORT_EMAIL}`),
    },
    {
      key: "faq",
      title: "FAQ",
      lines: ["Find answers to common", "questions and issues"],
      icon: <HelpCircle className="h-6 w-6" />,
      tint: "bg-emerald-50 text-emerald-600",
      cta: "View FAQ",
      onClick: () => navigate("/faq"),
    },
  ];

  return (
    <div className="crm-compact min-h-screen bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm">
          <Link to="/portal/dashboard" className="text-slate-500 hover:text-slate-800">Home</Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="text-slate-500">Help &amp; Support</span>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <span className="font-medium text-slate-800">Contact Us</span>
        </div>

        {/* Header */}
        <div className="mt-2">
          <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Contact Us</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            We are here to help! Choose the best way to get in touch with us.
          </p>
        </div>

        {/* Support channel cards */}
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {cards.map((c) => (
            <div
              key={c.key}
              className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white px-6 py-8 text-center shadow-sm transition-shadow hover:shadow-md"
            >
              <span className={`flex h-14 w-14 items-center justify-center rounded-2xl ${c.tint}`}>
                {c.icon}
              </span>
              <h3 className="mt-4 text-sm font-bold text-slate-900">{c.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-slate-500">
                {c.lines.map((l) => (
                  <span key={l} className="block">{l}</span>
                ))}
              </p>
              {c.linkText && (
                <a
                  href={c.linkHref}
                  className="mt-1 text-xs font-semibold text-blue-600 hover:underline"
                >
                  {c.linkText}
                </a>
              )}
              <span className="h-6 flex-shrink-0" aria-hidden />
              <button
                type="button"
                onClick={c.onClick}
                className={`mt-auto w-full rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                  c.primary
                    ? "bg-yellow-500 text-gray-900 shadow-sm hover:bg-yellow-600"
                    : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {c.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Other enquiries */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900">Other Enquiries</h3>
          <p className="mt-1.5 text-xs text-slate-500">
            For partnership, media or any other non-support related enquiries please email us at
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="mt-0.5 inline-block text-xs font-semibold text-blue-600 hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>
        </div>
      </main>
    </div>
  );
}

export default ContactUs;
